/* eslint-disable @typescript-eslint/no-explicit-any */
import AutomationRuleTrigger from './trigger';
import AutomationRuleConditions from './conditions';
import FormDividerConditions from './conditions/divider';
import FormDividerActions from './actions/divider';
import AutomationRuleActions from './actions';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { triggerTypes, useAutomationRuleTrigger } from './trigger/use-trigger';
import { useAutomationRuleConditions } from './conditions/use-conditions';
import { createNewAutomationRuleService } from 'src/services/appgami-api/create-automation-rule';
import { useAutomationRuleActions } from './actions/use-actions';
import { useAzureProvider } from 'src/azure/context';
import { AutomationRuleType } from 'src/services/appgami-api/types/automation-rule';
import { getAutomationRuleByIdService } from 'src/services/appgami-api/get-automation-rule-by-id';
import { updateAutomationRulesService } from 'src/services/appgami-api/update-automation-rules-service';
import { makeStyles, Toast, Toaster, ToastTitle, useId, useToastController } from '@fluentui/react-components';
import { useSearchParams, useBlocker, useNavigate } from 'react-router-dom';
import FormSkeleton from './form-skeleton';
import ConfirmToLeaveDialog from './confirm-to-leave-dialog';
import { findAutomationRuleSettingsService } from 'src/services/appgami-api/find-automation-rule-settings';

interface AutomationRuleFormContext {
	ruleId: string | null;
	actor: string;
	setActor: React.Dispatch<React.SetStateAction<string>>;
	title: string,
	setTitle: React.Dispatch<React.SetStateAction<string>>;
	createdBy: string;
	titleValidation: string | null;
	setTitleValidation: React.Dispatch<React.SetStateAction<string | null>>;
	status: string;
	setStatus: React.Dispatch<React.SetStateAction<string>>;
	tab: string;
	setTab: React.Dispatch<React.SetStateAction<string>>;
	loadAutomationRuleById(id: string): Promise<void>;
	saveAutomationRule(): Promise<void>;
	updateAutomationRule(id: string): Promise<void>;
	reset(): void;
	loadingPage: boolean;
	setLoadingPage: React.Dispatch<React.SetStateAction<boolean>>;
	resetValidations(): void;
	dispatchAlert(type: 'error' | 'success', text: string): void;
	submitting: boolean;
	setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}


const AutomationRuleFormContext = createContext({} as AutomationRuleFormContext);
export const useAutomationRuleFormProvider = () => useContext(AutomationRuleFormContext);

export default function AutomationRuleFormProvider(props: { children?: ReactNode | Iterable<ReactNode> }) {
	const navigate = useNavigate();
	const {applicationContext} = useAzureProvider();

	const toasterId = useId('automation-rule-toast');
	const { dispatchToast } = useToastController(toasterId);

	const [ruleId, setRuleId] = useState<string | null>(null);
	const [actor, setActor] = useState<string>('user');
	const [createdBy, setCreatedBy] = useState<string>('');

	const [loadingPage, setLoadingPage] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const [titleValidation, setTitleValidation] = useState<string | null>(null);

	const [title, setTitle] = useState('');
	const [status, setStatus] = useState('enabled');
	const [tab, setTab] = useState('rule');

	const [confirmToLeave, setConfirmToLeave] = useState({
		viewDialog: false,
	});

	const [, setUrlSearchParams] = useSearchParams();
	
	const {
		resetValidations: resetTriggerValidations,
		validate: validateTrigger,
		getValues: getTriggerValues,
		reset: resetTriggers,
		loadTrigger,
		trigger,
		triggerData
	} = useAutomationRuleTrigger();

	const {
		resetValidationErrors: resetConditionValidations,
		validate: validateConditions,
		getValues: getConditionValues,
		reset: resetConditions,
		loadConditions,
		conditions
	} = useAutomationRuleConditions();

	const {
		resetValidationErrors: resetActionValidations,
		validate: validateActions,
		getValues: getActionValues,
		reset: resetActions,
		loadActions,
		actions
	} = useAutomationRuleActions();

	const [updatingModeForm, setUpdatingModeForm] = useState<{
		state: 'no-started' | 'started' | 'updating',
		values: string // JSON
		updatedValues: string // JSON
	}>({
		state: 'no-started',
		values: '',
		updatedValues: ''
	});

	const conditionsToBlock = useMemo(() => {
		if (ruleId) {
			return [updatingModeForm.values !== updatingModeForm.updatedValues];
		}

		const results: Array<boolean> = [
			title !== '',
			trigger !== '',
			conditions.length > 0,
			actions.length > 0,
		];

		return results;
	}, [
		title,
		status,
		trigger,
		conditions,
		actions,
		updatingModeForm.state,
		updatingModeForm.values,
		updatingModeForm.updatedValues
	]);

	const navigationBlocker = useBlocker(
		({ currentLocation, nextLocation }) => {
			return currentLocation.pathname !== nextLocation.pathname && conditionsToBlock.some(isTrue => isTrue);
		}
	);

	const dispatchAlert = useCallback((type: 'error' | 'success' | 'info', text: string) => {
		dispatchToast(
			<Toast>
				<ToastTitle>{text}</ToastTitle>
			</Toast>,
			{ position: 'bottom-end', intent: type, timeout: 3000 }
		);
	}, []);

	useEffect(() => {
		if (updatingModeForm.state === 'started') {
			setUpdatingModeForm(oldValues => ({
				...oldValues,
				state: 'updating',
				values: JSON.stringify({
					name: title,
					organizationName: applicationContext.organizationName,
					organizationId: applicationContext.organizationId,
					projectId: applicationContext.projectId,
					status: status as any,
					trigger: getTriggerValues(),
					conditions: getConditionValues(),
					actions: getActionValues(),
				})
			}));
		}

		if (updatingModeForm.state === 'updating') {
			setUpdatingModeForm(oldValues => ({
				...oldValues,
				state: 'updating',
				updatedValues: JSON.stringify({
					name: title,
					organizationName: applicationContext.organizationName,
					organizationId: applicationContext.organizationId,
					projectId: applicationContext.projectId,
					status: status as any,
					trigger: getTriggerValues(),
					conditions: getConditionValues(),
					actions: getActionValues(),
				})
			}));
		}
	}, [
		updatingModeForm.state,
		updatingModeForm.updatedValues,
		status,
		title,
		trigger,
		triggerData,
		conditions,
		actions,
		applicationContext,
		ruleId
	]);

	const loadById = useCallback(async (id: string) => {
		setRuleId(id);
		const response = await getAutomationRuleByIdService(applicationContext.organizationName, applicationContext.projectId, id);
		if (response.status === 200) {
			const data = response.data.automation as AutomationRuleType;
			const conditions = data.conditions.filter(item => ![triggerTypes.workItemTransitions, triggerTypes.workItemIsAssignedTo].includes(item.type));
			setTitle(data.name);
			setStatus(data.status);
			await loadTrigger(data.trigger);
			await loadConditions(conditions);
			await loadActions(data.actions);
			setCreatedBy(data.created_by);
			setUpdatingModeForm(oldState => ({
				...oldState,
				state: 'started',
			}));

			const settingsResult = await findAutomationRuleSettingsService({
				projectId: applicationContext.projectId,
				organizationName: applicationContext.organizationName,
				automationRuleId: id,
			});

			if (settingsResult.type === 'success') {
				setActor(settingsResult.data.settings.actor.type);
			}

			if (settingsResult.type === 'error') {
				setActor('user');
			}
		}
	}, []);
	
	const newAutomationRule = useCallback(async () => {
		try {
			setSubmitting(true);
			resetValidations();
			const allValidations = [
				!title,
				validateTrigger(),
				!actions.length,
				await validateConditions(),
				await validateActions(),
			];

			if (!actions.length) {
				dispatchAlert('info', 'You must configure at least one action');
			}
	
			if (!title) {
				setTitleValidation('Automation rule title is required');
			}

			const noErrors = allValidations.every(hasError => !hasError);

			if (noErrors) {
				const data = {
					name: title,
					organizationName: applicationContext.organizationName,
					actor: {
						type: actor,
					},
					organizationId: applicationContext.organizationId,
					projectId: applicationContext.projectId,
					status: status as any,
					trigger: getTriggerValues(),
					conditions: getConditionValues(),
					actions: getActionValues(),
				};

				const response = await createNewAutomationRuleService(data);

				if (response.status === 200) {
					setSubmitting(true);
					// resetAll();
					setUrlSearchParams({id: response.data.automationId});
					setRuleId(response.data.automationId);
		
					const ruleResponse = await getAutomationRuleByIdService(applicationContext.organizationName, applicationContext.projectId, response.data.automationId);
					if (ruleResponse.status === 200) {
						setCreatedBy(ruleResponse.data.automation.created_by);
					}
					
					// await loadById(response.data.automationId);
				
					setUpdatingModeForm(old => ({...old, state: 'started' }));
					console.log('Call dispatchAlert success');
					dispatchAlert('success', 'Automation rule created successfully');
				}

				if (response.status === 403) {
					dispatchAlert('error', 'You don\'t have permission to create automation rules in this project.');
				}
			}			
		} catch {
			setSubmitting(false);
			dispatchAlert('error', 'Failed to create automation rule');
		} finally {
			setSubmitting(false);
			setConfirmToLeave(() => ({
				viewDialog: false
			}));
			navigationBlocker.state = 'unblocked';
		}
	}, [
		applicationContext,
		getTriggerValues,
		getConditionValues,
		getActionValues,
		validateConditions,
		validateActions,
		validateTrigger,
		title,
		status,
		conditions,
		actions,
		actor
	]);

	const updateAutomationRule = useCallback(async (id: string) => {
		try {
			setSubmitting(true);
			resetValidations();
			const allValidations = [
				!title,
				validateTrigger(),
				!actions.length,
				await validateConditions(),
				await validateActions(),
			];

			const noErrors = allValidations.every(hasError => !hasError);

			if (!actions.length) {
				dispatchAlert('info', 'You must configure at least one action');
			}

			if (!noErrors) {
				setSubmitting(false);
			}
		
			if (noErrors) {
				const data = {
					name: title,
					organizationName: applicationContext.organizationName,
					organizationId: applicationContext.organizationId,
					projectId: applicationContext.projectId,
					actor: actor,
					status: status as any,
					trigger: getTriggerValues(),
					conditions: getConditionValues(),
					actions: getActionValues(),
				};
	
				const response = await updateAutomationRulesService({
					projectId: applicationContext.projectId,
					organizationName: applicationContext.organizationName,
					automationRuleList: [{ ...data, id: id, actor: actor as 'user' | 'service-principal' }]
				});

				if (
					response &&
					response.status === 403 && 
					response.data?.code === 'MISSING_USER_PERMISSION_TO_UPDATE_AUTOMATION_RULE'
				) {
					dispatchAlert('error', 'You don\'t have permission to update automation rules in this project.');
				}

				if (response && response.status === 204) {
					dispatchAlert('success', 'Automation rule updated successfully');
				}

				if (response && response.status === 400) {
					dispatchAlert('error', 'Failed to update automation rule');
				}

				// resetAll();
				// await loadById(id);
			} 
		} catch {
			//
			dispatchAlert('error', 'Failed to update automation rule');
			setSubmitting(false);
		} finally {
			setSubmitting(false);
			setConfirmToLeave(() => ({
				viewDialog: false
			}));
			const values = {
				name: title,
				organizationName: applicationContext.organizationName,
				organizationId: applicationContext.organizationId,
				projectId: applicationContext.projectId,
				status: status as any,
				trigger: getTriggerValues(),
				conditions: getConditionValues(),
				actions: getActionValues(),
			};
			setUpdatingModeForm(oldValues => ({
				...oldValues,
				values: JSON.stringify(values),
				updatedValues: JSON.stringify(values)
			}));
			navigationBlocker.state = 'unblocked';
			
		}
	}, [
		applicationContext,
		getTriggerValues,
		getConditionValues,
		getActionValues,
		validateConditions,
		validateActions,
		validateTrigger,
		title,
		status,
		conditions,
		actions,
		actor
	]);

	const resetAll = useCallback(() => {
		resetTriggers();
		resetConditions();
		resetActions();
	}, []);

	const resetValidations = useCallback(() => {
		setTitleValidation(null);
		resetTriggerValidations();
		resetConditionValidations();
		resetActionValidations();
	}, []);

	useEffect(() => {
		if (navigationBlocker.state === 'blocked') {
			setConfirmToLeave(oldValues => ({
				...oldValues,
				viewDialog: true
			}));
		}

		return () => {
			navigationBlocker.state = 'unblocked';
		};
	}, [
		navigationBlocker
	]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (conditionsToBlock.some(isTrue => isTrue)) {
				event.preventDefault();
				event.returnValue = '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [conditionsToBlock]);

	return (
		<>
			<AutomationRuleFormContext.Provider value={{
				loadAutomationRuleById: loadById,
				saveAutomationRule: newAutomationRule,
				updateAutomationRule,
				reset: resetAll,
				ruleId,
				loadingPage,
				setLoadingPage,
				title,
				setTitle,
				titleValidation,
				setTitleValidation,
				tab,
				setTab,
				status,
				setStatus,
				resetValidations,
				dispatchAlert,
				submitting,
				setSubmitting,
				actor,
				setActor,
				createdBy,
			}}>
				<ConfirmToLeaveDialog
					open={confirmToLeave.viewDialog}
					onConfirm={() => {
						navigationBlocker.state = 'proceeding';
						setConfirmToLeave(() => ({
							viewDialog: false
						}));
						setTimeout(() => {
							navigate('/');
						}, 0);
					}}
					onOpenChange={(value) => {
						setConfirmToLeave(() => ({
							viewDialog: value
						}));
					}}
				/>
				<Toaster toasterId={toasterId} />
				{props?.children}
			</AutomationRuleFormContext.Provider>
		</>
	);
}

export function AutomationRuleForm() {
	const {loadingPage} = useAutomationRuleFormProvider();
	const classes = useClasses();

	return (
		<>
			{
				loadingPage && (
					<FormSkeleton />
				)
			}
			{
				!loadingPage && (
					<div className={classes.root}>
						<AutomationRuleTrigger />
						<FormDividerConditions />
						<AutomationRuleConditions />
						<FormDividerActions itsFirst />
						<AutomationRuleActions />
					</div>
				)
			}
		</>
	);
}

export const useClasses = makeStyles({
	root: {
		position: 'relative',
		width: '100%',
		height: '100%',
		zIndex: 0,
	},
	submitLoading: {
		position: 'absolute',
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
		backgroundColor: 'gray'
	}
});